import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import { attachInvoicesToQuota } from "~/modules/quota/api/quota/quotaApiDispatchers.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { EligibleQuotaInvoice, QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import {
	QuotaInvoiceSelector,
} from "~/modules/quota/components/CreateQuotaSidebar/components/QuotaInvoiceSelector.tsx";
import { QuotaInvoicesFormSection } from "~/modules/quota/components/formSections/QuotaInvoicesFormSection.tsx";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations;
};

// Attaches invoices to an invoice-backed quota. It sends the invoice ids only (backend ticket 18).
// No budget rule applies: each excess is > 0, so an attach only raises the amount, and a shell has no consumers.
export const AttachQuotaInvoicesForm = ({ onCancel, onSuccess, quota }: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
	const [showInvoiceSelect, setShowInvoiceSelect] = useState(false);

	const {
		data: eligibleInvoices,
		isLoading: eligibleInvoicesIsLoading,
	} = useQuery(quotaQueries.eligibleInvoices(quota.companyId));

	const selectedInvoices = useMemo(() => {
		const invoiceById = new Map((eligibleInvoices ?? []).map((invoice) => [invoice.id, invoice]));
		return selectedInvoiceIds
			.map((invoiceId) => invoiceById.get(invoiceId))
			.filter((invoice): invoice is EligibleQuotaInvoice => !!invoice);
	}, [eligibleInvoices, selectedInvoiceIds]);

	const isShell = quota.invoices.length === 0;
	const selectedExcessCents = selectedInvoices.reduce((sum, invoice) => sum + invoice.excessCents, 0);
	// The backend recomputes the amount from all attached invoices. On a shell, the excess replaces the placeholder.
	const newAmountCents = (isShell ? 0 : quota.amountCents ?? 0) + selectedExcessCents;

	const handleConfirmInvoices = useCallback((invoiceIds: string[]) => {
		setSelectedInvoiceIds((prevState) => [...prevState, ...invoiceIds]);
		setShowInvoiceSelect(false);
	}, []);

	const handleRemoveInvoice = useCallback((invoiceId: string) => {
		setSelectedInvoiceIds((prevState) => prevState.filter((id) => id !== invoiceId));
	}, []);

	const onSubmit = useCallback(async () => {
		try {
			setIsBusy(true);
			await attachInvoicesToQuota(quota.companyId, quota.id, { invoiceIds: selectedInvoiceIds });
			onSuccess();
		} catch (error) {
			// The backend gives the definite answer, for example on an invoice that another quota took meanwhile.
			const apiError = handleApiError(error);
			if (apiError.type === ApiResponseErrorType.VALIDATION) {
				setServerErrorMessage(Object.values(apiError.messages)
					.flat()
					.map((detail) => detail.message)
					.join("\n"));
			} else {
				setServerErrorMessage(t("form:unexpectedError"));
			}
			setIsBusy(false);
		}
	}, [onSuccess, quota.companyId, quota.id, selectedInvoiceIds]);

	if (showInvoiceSelect) {
		return <div className="auto-grid-sidebar">
			<QuotaInvoiceSelector invoices={eligibleInvoices}
								  isLoading={eligibleInvoicesIsLoading}
								  excludedInvoiceIds={selectedInvoiceIds}
								  onCancel={() => setShowInvoiceSelect(false)}
								  onConfirm={handleConfirmInvoices} />
		</div>;
	}

	return (
		<div className="auto-grid-sidebar">
			<SidebarHeader>
				<SidebarHeaderHeadline>Rechnungen hinzufügen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Hinzufügen fehlgeschlagen">
						<span className="whitespace-pre-line">{serverErrorMessage}</span>
					</SidebarErrorOverlay>}
				<FormSectionGroup>
					<QuotaInvoicesFormSection invoices={selectedInvoices}
											  onAddClick={() => setShowInvoiceSelect(true)}
											  onRemove={handleRemoveInvoice} />
					{selectedInvoices.length > 0 && <FormSection title="Budget">
						<div>
							<div className="text-lg font-medium">{formatCentsToCurrency(newAmountCents)}</div>
							<div className="text-sm text-gray-500">
								{isShell
									? "Das Budget ergibt sich aus der Summe der Überschüsse der ausgewählten Rechnungen."
									: `Bisher ${formatCentsToCurrency(quota.amountCents ?? 0)}, dazu ${formatCentsToCurrency(selectedExcessCents)} aus den ausgewählten Rechnungen.`}
							</div>
						</div>
						{isShell && quota.amountCents !== null &&
							<Hint>
								Das Platzhalter-Budget von {formatCentsToCurrency(quota.amountCents)} wird durch die
								Überschüsse der Rechnungen ersetzt.
							</Hint>}
					</FormSection>}
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<Button disabled={selectedInvoiceIds.length === 0 || isBusy}
						onClick={onSubmit}>
					Hinzufügen
				</Button>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</div>);
};
